import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Chart from 'chart.js';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

import blue from '@material-ui/core/colors/blue';
import yellow from '@material-ui/core/colors/yellow';
import red from '@material-ui/core/colors/red';
import orange from '@material-ui/core/colors/orange';
import green from '@material-ui/core/colors/green';
import purple from '@material-ui/core/colors/purple';

const useStyles = makeStyles(theme => ({
    section: {
        padding: theme.spacing(3)
    },
    canvasContainer: {
        position: 'relative',
        width: '100%',
    },
    container: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    textGreen: {
        color: green[600]
    },
    marginTop: {
        marginTop: theme.spacing(3)
    }
}));

let ctx = null;
let analyticsChart = null;

export default function General(props) {

    const {
        event,
        tickets,
        groups,
        loading,
        setLoading
    } = props;

    const classes = useStyles();

    useEffect(() => {

        const initialConfig = {
            type: 'doughnut',
            data: {
                labels: [
                    'Pending',
                    'Accepted',
                    'Maybe',
                    'Withdrawn',
                    'Declined',
                    'Pay Later'
                ],
                datasets: [{
                    data: [
                        event.invitations_pending,
                        event.invitations_accepted,
                        event.invitations_maybe,
                        event.invitations_withdrawn,
                        event.invitations_declined,
                        event.invitations_pay_later
                    ],
                    backgroundColor: [
                        blue[500],
                        green[500],
                        purple[500],
                        yellow[700],
                        red[500],
                        orange[600]
                    ],
                    hoverOffset: 10
                }]
            },
            options: {
                legend: {
                    position: 'left'
                },
            }
        };

        ctx = document.getElementById('analyticsGraph').getContext("2d");
        analyticsChart = new Chart(ctx, initialConfig);

    }, []);

    const eventLengthString = useMemo(() => {
        const el = event.ends_at.diff(event.starts_at, ['minutes', 'hours', 'days']).toObject();
        console.log(el);
        let s = '';

        if (el.days > 0) {
            const days = el.days > 1 ? 'days' : 'day';
            s += `${el.days} ${days} `;
        }

        if  (el.hours > 0) {
            const hours = el.hours > 1 ? 'hours' : 'hour';
            s += `${el.hours} ${hours} `;
        }

        if (el.minutes > 0) {
            const minutes = el.minutes > 1 ? 'minutes' : 'minute';
            s += `${el.minutes} ${minutes}`;
        }

        return s;
    }, [event]);

    const eventGroup = useMemo(() => {
        return event.group_id ? groups.find(group => group.id === event.group_id).title : 'All Users';
    }, [event]);

    return (
        <div className={classes.section}>
            <Grid
                container
                spacing={3}
                >
                <Grid item xs={6}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        Event Invitations by Status
                    </Typography>
                    <div className={classes.canvasContainer}>
                        <canvas id="analyticsGraph"></canvas>
                    </div>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        Event Information
                    </Typography>
                    <Divider/>
                    <Typography
                        className={classes.marginTop}
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                        >
                        {event.short_description}
                    </Typography>
                    <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                        spacing={1}
                        className={classes.container}
                        >
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Event {(event.starts_at > DateTime.local()) ? 'Begins' : 'Began'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                {event.starts_at.toLocaleString(DateTime.DATETIME_FULL)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Event Duration
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                {eventLengthString}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Location
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                {event.location}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                        spacing={1}
                        className={classes.container}
                        >
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Group Tickets Required
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="error">
                                {event.group_tickets_required ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Guest Tickets Required
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="error">
                                {event.guest_tickets_required ? 'Yes' : 'No'}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                        spacing={1}
                        className={classes.container}
                        >
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Total Invitations
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {event.invitations_count}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Invitations Accepted
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" className={classes.textGreen}>
                                {event.invitations_accepted} Invitations | {event.guests_accepted} Guests
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Guests Entered
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" className={classes.textGreen}>
                                {event.invitations_entered} Invitations | {event.guests_entered} Guests
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                spacing={3}
                >
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        Event Earnings
                    </Typography>
                    <Divider/>
                    <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                        spacing={1}
                        className={classes.container}
                        >
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Gross Total Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                ${event.earnings.gross.toFixed(2)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Amount Paid
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                ${event.earnings.paid.toFixed(2)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                Amount Refunded
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                ${event.earnings.refunded.toFixed(2)}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <Typography variant="subtitle2" className={classes.textGreen}>
                                Net Total Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="secondary">
                                ${event.earnings.net.toFixed(2)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>

    );

}
